import { z } from 'zod';

import { broadcastVariableOwnershipOptions } from './broadcastVariables';

export const metaTemplateCategoryOptions = ['MARKETING', 'UTILITY', 'AUTHENTICATION'] as const;

export const WHATSAPP_BROADCAST_UNSUBSCRIBE_FOOTER = 'To unsubscribe from broadcasts, reply STOP.';

export const whatsAppTemplateSourceOptions = ['seed', 'local', 'meta_imported'] as const;

export const whatsAppTemplateSyncStatusOptions = [
  'LOCAL_ONLY',
  'SYNCED',
  'OUT_OF_SYNC',
  'SYNC_ERROR',
  'META_ONLY',
] as const;

export const whatsAppMetaTemplateStatusEventOptions = [
  'APPROVED',
  'IN_APPEAL',
  'PENDING',
  'IN_REVIEW',
  'SUBMITTED',
  'REJECTED',
  'PENDING_DELETION',
  'DELETED',
  'DISABLED',
  'PAUSED',
  'FLAGGED',
  'REINSTATED',
] as const;

export type WhatsAppMetaTemplateStatusEvent =
  (typeof whatsAppMetaTemplateStatusEventOptions)[number];

export const normalizeWhatsAppMetaTemplateStatusEvent = (
  value: unknown
): WhatsAppMetaTemplateStatusEvent | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().toUpperCase();
  return whatsAppMetaTemplateStatusEventOptions.includes(
    normalized as WhatsAppMetaTemplateStatusEvent
  )
    ? (normalized as WhatsAppMetaTemplateStatusEvent)
    : null;
};

export const whatsAppTemplateLifecycleStatusOptions = [
  'draft',
  'local_only',
  'pending',
  'approved',
  'rejected',
  'disabled',
  'sync_error',
  'meta_only',
] as const;

export const whatsAppTemplateHeaderFormatOptions = ['TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'] as const;
export const whatsAppTemplateParameterFormatOptions = ['NAMED', 'POSITIONAL'] as const;

export const whatsAppTemplateButtonTypeOptions = [
  'QUICK_REPLY',
  'URL',
  'PHONE_NUMBER',
  'COPY_CODE',
  'FLOW',
] as const;

export const whatsAppTemplateMediaHeaderFormatOptions = ['IMAGE', 'VIDEO', 'DOCUMENT'] as const;
export type WhatsAppTemplateMediaHeaderFormat =
  (typeof whatsAppTemplateMediaHeaderFormatOptions)[number];

/** Meta limits that the builder UI and server validation share. */
export const whatsAppTemplateLimits = {
  headerText: 60,
  body: 1024,
  footer: 60,
  buttonText: 25,
  buttonUrl: 2000,
  buttonPhone: 20,
  copyCode: 15,
  maxButtons: 10,
  maxQuickReplies: 10,
  maxUrlButtons: 2,
  maxPhoneButtons: 1,
  maxCopyCodeButtons: 1,
} as const;

const whatsAppTemplateProductPreviewSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const templateVariableDefinitionSchema = z.object({
  key: z.string().min(1).max(120),
  label: z.string().max(255).optional(),
  description: z.string().max(500).optional(),
  required: z.boolean().optional(),
  ownership: z.enum(broadcastVariableOwnershipOptions).optional(),
  mappedKey: z.string().min(1).max(120).optional(),
});

export const templateNameSchema = z
  .string()
  .min(1)
  .max(191)
  .regex(
    /^[a-z0-9_]+$/,
    'Template names must use lowercase letters, numbers, and underscores only.'
  );

export const templateKeySchema = z
  .string()
  .min(1)
  .max(191)
  .regex(
    /^[a-z0-9._-]+$/,
    'Template keys must use lowercase letters, numbers, dots, hyphens, or underscores.'
  );

export const templateLanguageSchema = z
  .string()
  .min(2)
  .max(20)
  .regex(/^[a-z]{2}(?:[_-][A-Za-z]{2})?$/, 'Language must look like en-IN, hi-IN, or bn-IN.');

export const quickReplyButtonSchema = z.object({
  type: z.literal('QUICK_REPLY'),
  text: z.string().min(1).max(25),
});

export const urlButtonSchema = z.object({
  type: z.literal('URL'),
  text: z.string().min(1).max(25),
  url: z.string().min(1).max(2000),
});

export const phoneNumberButtonSchema = z.object({
  type: z.literal('PHONE_NUMBER'),
  text: z.string().min(1).max(25),
  phoneNumber: z.string().min(5).max(50),
});

/**
 * Copy-code buttons let the recipient copy a coupon with one tap. The code itself
 * is supplied per broadcast (campaign variable `coupon_code`), Meta only needs an example.
 */
export const copyCodeButtonSchema = z.object({
  type: z.literal('COPY_CODE'),
  text: z.string().max(25).optional().default('Copy offer code'),
  example: z.string().min(1).max(15),
});

export const whatsAppTemplateButtonSchema = z.discriminatedUnion('type', [
  quickReplyButtonSchema,
  urlButtonSchema,
  phoneNumberButtonSchema,
  copyCodeButtonSchema,
  /**
   * Flow buttons are attached in Meta, never built here. Meta omits `flow_id` and
   * `flow_action` when it returns template components, so an imported button carries
   * only its label; both fields stay optional rather than failing the read.
   */
  z.object({
    type: z.literal('FLOW'),
    text: z.string().min(1).max(25),
    flowId: z.string().regex(/^\d*$/).optional().default(''),
    flowAction: z.enum(['navigate', 'data_exchange']).default('navigate'),
    navigateScreen: z.string().optional(),
  }),
]);

export const headerComponentSchema = z.object({
  type: z.literal('HEADER'),
  format: z.enum(whatsAppTemplateHeaderFormatOptions),
  text: z.string().max(256).optional(),
  // Meta handles are ~230 chars today; leave headroom without accepting arbitrary blobs.
  exampleMediaHandle: z.string().max(1024).optional().nullable(),
  /** Sample file metadata kept for the builder preview; never sent to Meta. */
  exampleMediaFileName: z.string().max(255).optional().nullable(),
  exampleMediaMimeType: z.string().max(120).optional().nullable(),
});

export const bodyComponentSchema = z.object({
  type: z.literal('BODY'),
  text: z.string().min(1).max(1024),
});

export const footerComponentSchema = z.object({
  type: z.literal('FOOTER'),
  text: z.string().min(1).max(60),
});

export const buttonsComponentSchema = z.object({
  type: z.literal('BUTTONS'),
  buttons: z.array(whatsAppTemplateButtonSchema).min(1).max(10),
});

export const whatsAppTemplateComponentSchema = z.discriminatedUnion('type', [
  headerComponentSchema,
  bodyComponentSchema,
  footerComponentSchema,
  buttonsComponentSchema,
]);

/** Immutable display snapshot; values are limited to template placeholders, never provider credentials. */
export const whatsAppTemplateMessageSchema = z.object({
  components: z.array(whatsAppTemplateComponentSchema),
  values: z.record(z.string()),
  bodyText: z.string(),
  media: z
    .object({
      url: z.string(),
      fileName: z.string().nullable(),
      mimeType: z.string().nullable(),
    })
    .optional(),
});

export type WhatsAppTemplateMessage = z.infer<typeof whatsAppTemplateMessageSchema>;

export function getWhatsAppTemplateMessage(providerPayload: unknown) {
  const result = z
    .object({ whatsappTemplate: whatsAppTemplateMessageSchema })
    .safeParse(providerPayload);
  return result.success ? result.data.whatsappTemplate : null;
}

export const whatsAppTemplateComponentsSchema = z
  .array(whatsAppTemplateComponentSchema)
  .min(1)
  .superRefine((components, context) => {
    const bodyComponents = components.filter(component => component.type === 'BODY');
    const headerComponents = components.filter(component => component.type === 'HEADER');
    const footerComponents = components.filter(component => component.type === 'FOOTER');
    const buttonsComponents = components.filter(component => component.type === 'BUTTONS');

    if (bodyComponents.length !== 1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Templates must include exactly one body component.',
      });
    }

    if (headerComponents.length > 1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Templates can include only one header component.',
      });
    }

    if (footerComponents.length > 1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Templates can include only one footer component.',
      });
    }

    if (buttonsComponents.length > 1) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Templates can include only one buttons component.',
      });
    }

    headerComponents.forEach(component => {
      if (component.format === 'TEXT' && !component.text?.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Text headers require header text.',
        });
      }

      if (component.format !== 'TEXT' && component.text?.trim()) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Media headers cannot include text content.',
        });
      }
    });

    buttonsComponents.forEach(component => {
      const quickReplies = component.buttons.filter(button => button.type === 'QUICK_REPLY');
      const urlButtons = component.buttons.filter(button => button.type === 'URL');
      const phoneButtons = component.buttons.filter(button => button.type === 'PHONE_NUMBER');
      const copyCodeButtons = component.buttons.filter(button => button.type === 'COPY_CODE');

      if (quickReplies.length > whatsAppTemplateLimits.maxQuickReplies) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Meta allows at most ${whatsAppTemplateLimits.maxQuickReplies} quick reply buttons.`,
        });
      }

      if (urlButtons.length > whatsAppTemplateLimits.maxUrlButtons) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Meta allows at most ${whatsAppTemplateLimits.maxUrlButtons} website buttons.`,
        });
      }

      if (phoneButtons.length > whatsAppTemplateLimits.maxPhoneButtons) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Meta allows only one call button.',
        });
      }

      if (copyCodeButtons.length > whatsAppTemplateLimits.maxCopyCodeButtons) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Meta allows only one copy-code button.',
        });
      }

      // Quick replies must be grouped together (all at the start or all at the end).
      const kinds = component.buttons.map(button => (button.type === 'QUICK_REPLY' ? 'q' : 'c'));
      const groupChanges = kinds.filter((kind, index) => index > 0 && kind !== kinds[index - 1]);
      if (groupChanges.length > 1) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Quick reply buttons must be grouped together, not mixed between website or call buttons.',
        });
      }
    });
  });

export const whatsAppTemplateDraftSchema = z.object({
  templateKey: templateKeySchema,
  title: z.string().min(1).max(255),
  category: z.string().min(1).max(100),
  eventKey: z.string().max(191).optional().nullable(),
  productId: z.coerce.number().int().positive().optional().nullable(),
  language: templateLanguageSchema,
  metaCategory: z.enum(metaTemplateCategoryOptions),
  providerTemplateName: templateNameSchema,
  bodyTemplate: z.string().min(1).max(1024),
  bodyPreviewText: z.string().max(1024).optional().nullable(),
  variableDefinitions: z.array(templateVariableDefinitionSchema).default([]),
  samplePayload: z.record(z.unknown()).default({}),
  components: whatsAppTemplateComponentsSchema,
  isActive: z.boolean().default(true),
});

export const whatsAppTemplatePreviewButtonSchema = z.object({
  type: z.enum(whatsAppTemplateButtonTypeOptions),
  text: z.string(),
  renderedValue: z.string().optional().nullable(),
});

export const whatsAppTemplatePreviewSchema = z.object({
  header: z.string().optional().nullable(),
  body: z.string(),
  footer: z.string().optional().nullable(),
  buttons: z.array(whatsAppTemplatePreviewButtonSchema),
});

export const whatsAppTemplateMetaSnapshotSchema = z.object({
  id: z.string().optional().nullable(),
  name: z.string(),
  language: z.string().optional().nullable(),
  category: z.string().optional().nullable(),
  status: z.string().optional().nullable(),
  parameterFormat: z.enum(whatsAppTemplateParameterFormatOptions).optional().nullable(),
  rejectionReason: z.string().optional().nullable(),
  components: whatsAppTemplateComponentsSchema.default([]),
});

export const whatsAppTemplateComparisonSchema = z.object({
  exactMatch: z.boolean(),
  differences: z.array(z.string()),
});

export const whatsAppTemplateSummarySchema = z.object({
  id: z.number(),
  adminId: z.number().nullable().optional(),
  /** WhatsApp Business Account the template belongs to (null for legacy/platform templates). */
  wabaId: z.string().nullable().optional(),
  templateKey: z.string(),
  title: z.string(),
  category: z.string(),
  eventKey: z.string().nullable(),
  channel: z.literal('whatsapp'),
  productId: z.number().nullable(),
  product: whatsAppTemplateProductPreviewSchema.nullable(),
  language: z.string(),
  metaCategory: z.string().nullable(),
  providerTemplateName: z.string().nullable(),
  providerTemplateId: z.string().nullable(),
  metaTemplateStatus: z.string().nullable(),
  metaParameterFormat: z.enum(whatsAppTemplateParameterFormatOptions).nullable(),
  metaRejectionReason: z.string().nullable(),
  metaLanguageCount: z.number().int().nonnegative().default(0),
  metaLanguages: z.array(z.string()).default([]),
  bodyTemplate: z.string(),
  bodyPreviewText: z.string().nullable(),
  variableDefinitions: z.array(templateVariableDefinitionSchema),
  samplePayload: z.record(z.unknown()),
  components: whatsAppTemplateComponentsSchema,
  isActive: z.boolean(),
  isDraft: z.boolean(),
  source: z.enum(whatsAppTemplateSourceOptions),
  syncStatus: z.enum(whatsAppTemplateSyncStatusOptions),
  lastSyncError: z.string().nullable(),
  lastSyncedAt: z.string().nullable(),
  lastPushedAt: z.string().nullable(),
  versionNumber: z.number(),
  parentTemplateId: z.number().nullable(),
  editableOnMeta: z.boolean(),
  lifecycleStatus: z.enum(whatsAppTemplateLifecycleStatusOptions),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});

export const whatsAppTemplateDetailSchema = whatsAppTemplateSummarySchema.extend({
  preview: whatsAppTemplatePreviewSchema,
  metaTemplate: whatsAppTemplateMetaSnapshotSchema.nullable(),
  comparison: whatsAppTemplateComparisonSchema,
});

export const whatsAppTemplateValidationSchema = z.object({
  valid: z.boolean(),
  errors: z.array(z.string()),
  warnings: z.array(z.string()),
  placeholders: z.array(z.string()),
  preview: whatsAppTemplatePreviewSchema.nullable(),
});

/** Result of uploading a sample header file for Meta template review. */
export const whatsAppTemplateSampleMediaSchema = z.object({
  handle: z.string().min(1),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  format: z.enum(whatsAppTemplateMediaHeaderFormatOptions),
});

export type WhatsAppTemplateSampleMedia = z.infer<typeof whatsAppTemplateSampleMediaSchema>;

export type TemplateVariableDefinition = z.infer<typeof templateVariableDefinitionSchema>;
export type WhatsAppTemplateButton = z.infer<typeof whatsAppTemplateButtonSchema>;
export type WhatsAppTemplateComponent = z.infer<typeof whatsAppTemplateComponentSchema>;

/** Company broadcast drafts use one fixed footer; Meta-linked snapshots stay unchanged. */
export function withBroadcastUnsubscribeFooter(
  components: WhatsAppTemplateComponent[]
): WhatsAppTemplateComponent[] {
  const result: WhatsAppTemplateComponent[] = components.filter(
    component => component.type !== 'FOOTER'
  );
  const buttonsIndex = result.findIndex(component => component.type === 'BUTTONS');
  result.splice(buttonsIndex < 0 ? result.length : buttonsIndex, 0, {
    type: 'FOOTER',
    text: WHATSAPP_BROADCAST_UNSUBSCRIBE_FOOTER,
  });
  return result;
}

export type WhatsAppTemplateDraft = z.infer<typeof whatsAppTemplateDraftSchema>;
export type WhatsAppTemplatePreview = z.infer<typeof whatsAppTemplatePreviewSchema>;
export type WhatsAppTemplateMetaSnapshot = z.infer<typeof whatsAppTemplateMetaSnapshotSchema>;
export type WhatsAppTemplateSummary = z.infer<typeof whatsAppTemplateSummarySchema>;
export type WhatsAppTemplateDetail = z.infer<typeof whatsAppTemplateDetailSchema>;
export type WhatsAppTemplateValidationResult = z.infer<typeof whatsAppTemplateValidationSchema>;
